/** Insurance routes (IA §2 Improve › Insurance). Every view's state is in the URL. */
import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { appRoute } from '@/app/appRoute';
import { str } from '@/lib/search';

const oneOf = <T extends string>(v: unknown, allowed: readonly T[]): T | undefined => (allowed.includes(v as T) ? (v as T) : undefined);

export interface RiskSearch {
  granularity?: 'week';
  dimension?: string;
}

export interface DriversSearch {
  q?: string;
  band?: 'low' | 'moderate' | 'elevated' | 'high';
  order?: 'best';
  sort?: string;
  selected?: string;
}

export interface ClaimsSearch {
  q?: string;
  status?: 'open' | 'submitted' | 'accepted' | 'rejected' | 'closed';
  sort?: string;
  /** Open the "new claim" dialog: by hand, or from a detected event (first notice of loss). */
  create?: 'manual' | 'event';
  /** Pre-select this detection in the FNOL dialog (links from events use it). */
  fnol?: string;
}

export const insuranceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/insurance',
  staticData: { permission: ['insurance.risk.read', 'insurance.claim.read'] },
  validateSearch: (s: Record<string, unknown>): RiskSearch => ({ granularity: s.granularity === 'week' ? 'week' : undefined, dimension: str(s.dimension) }),
  component: lazyRouteComponent(() => import('./pages/RiskPage')),
});

export const driversRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/insurance/drivers',
  staticData: { permission: 'insurance.risk.read' },
  validateSearch: (s: Record<string, unknown>): DriversSearch => ({
    q: str(s.q),
    band: oneOf(s.band, ['low', 'moderate', 'elevated', 'high'] as const),
    order: s.order === 'best' ? 'best' : undefined,
    sort: str(s.sort),
    selected: str(s.selected),
  }),
  component: lazyRouteComponent(() => import('./pages/DriversPage')),
});

export const coachingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/insurance/coaching',
  staticData: { permission: 'insurance.risk.read' },
  component: lazyRouteComponent(() => import('./pages/CoachingPage')),
});

export const claimsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/insurance/claims',
  staticData: { permission: 'insurance.claim.read' },
  validateSearch: (s: Record<string, unknown>): ClaimsSearch => ({
    q: str(s.q),
    status: oneOf(s.status, ['open', 'submitted', 'accepted', 'rejected', 'closed'] as const),
    sort: str(s.sort),
    create: oneOf(s.create, ['manual', 'event'] as const) ?? (str(s.fnol) ? 'event' : undefined),
    fnol: str(s.fnol),
  }),
  component: lazyRouteComponent(() => import('./pages/ClaimsPage')),
});

export const claimRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/insurance/claims/$claimId',
  staticData: { permission: 'insurance.claim.read' },
  component: lazyRouteComponent(() => import('./pages/ClaimPage')),
});
